import React, { useState, useEffect } from 'react';
import { colors, radius, shadows } from '../../styles/theme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}`, ...extra } : extra;
};

export default function RebuildHistoryModal({ donusumId, onClose, onSuccess }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchPreview = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/birim-donusumleri/${donusumId}/rebuild-history?dry_run=true`,
          { headers: getAuthHeaders() }
        );
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setError(body.detail || 'Önizleme alınamadı');
        } else {
          setPreview(body);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Önizleme alınamadı');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPreview();
    return () => { cancelled = true; };
  }, [donusumId]);

  const handleConfirm = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(
        `${API_URL}/api/birim-donusumleri/${donusumId}/rebuild-history`,
        { method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        if (onSuccess) onSuccess(body);
        onClose();
      } else {
        setSubmitError(body.detail || 'Bilinmeyen hata');
      }
    } catch (err) {
      setSubmitError(err.message || 'Ağ hatası');
    } finally {
      setSubmitting(false);
    }
  };

  const ozet = preview?.ozet;
  const donusturulecekSayisi = ozet?.donusturulecek || 0;
  const canConfirm = !loading && !error && !submitting && donusturulecekSayisi > 0;

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        background: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={() => { if (!submitting) onClose(); }}
    >
      <div
        style={{
          background: colors.surface, borderRadius: radius.lg, padding: 24,
          width: '600px', maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto',
          boxShadow: shadows.xl,
        }}
        onClick={e => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: colors.textPrimary }}>
          Geçmişi Düzelt — Önizleme
        </h3>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: colors.textSecondary }}>
          Bu dönüşüm tanımlanmadan önce kaydedilmiş fiyat geçmişi satırları yeni katsayı ile dönüştürülecek.
        </p>

        {loading && (
          <div style={{ padding: 32, textAlign: 'center', color: colors.textTertiary }}>
            Yükleniyor...
          </div>
        )}

        {!loading && error && (
          <div style={{
            padding: '12px 16px', borderRadius: radius.md,
            background: colors.dangerLight, color: '#991b1b',
            border: `1px solid ${colors.danger}`, marginBottom: 16, fontSize: 14,
          }}>
            {error}
          </div>
        )}

        {!loading && !error && preview && (
          <>
            {/* Malzeme + dönüşüm bilgisi */}
            <div style={{
              padding: '12px 16px', background: colors.backgroundAlt,
              borderRadius: radius.md, marginBottom: 16, fontSize: 14,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 6, color: colors.textPrimary }}>
                {preview.malzeme_ad}
              </div>
              <div style={{ color: colors.textSecondary }}>
                <span style={{ background: '#fff3e0', padding: '2px 8px', borderRadius: 4, fontWeight: 600, fontSize: 12 }}>
                  {preview.alis_birimi}
                </span>
                <span style={{ margin: '0 8px', color: colors.textTertiary }}>→</span>
                <span style={{ background: '#e8f5e9', padding: '2px 8px', borderRadius: 4, fontWeight: 600, fontSize: 12 }}>
                  {preview.stok_birimi}
                </span>
                <span style={{ marginLeft: 12, fontWeight: 700, color: colors.info }}>
                  ×{preview.katsayi}
                </span>
              </div>
            </div>

            {/* Özet */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16,
            }}>
              <div style={{
                padding: 12, background: colors.backgroundAlt,
                borderRadius: radius.md, textAlign: 'center',
              }}>
                <div style={{ fontSize: 11, color: colors.textTertiary, fontWeight: 600, textTransform: 'uppercase' }}>Toplam</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary }}>{ozet.toplam}</div>
              </div>
              <div style={{
                padding: 12, background: colors.infoLight,
                borderRadius: radius.md, textAlign: 'center',
              }}>
                <div style={{ fontSize: 11, color: '#075985', fontWeight: 600, textTransform: 'uppercase' }}>Dönüştürülecek</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: colors.info }}>{ozet.donusturulecek}</div>
              </div>
              <div style={{
                padding: 12, background: colors.warningLight,
                borderRadius: radius.md, textAlign: 'center',
              }}>
                <div style={{ fontSize: 11, color: '#92400e', fontWeight: 600, textTransform: 'uppercase' }}>Atlanan</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: colors.warning }}>{ozet.atlanan}</div>
              </div>
            </div>

            {/* Dönüştürülecek kayıtlar */}
            {donusturulecekSayisi > 0 ? (
              <div style={{
                border: `1px solid ${colors.border}`, borderRadius: radius.md,
                overflow: 'hidden', marginBottom: 16,
              }}>
                <div style={{
                  padding: '8px 12px', background: colors.backgroundAlt,
                  fontSize: 12, fontWeight: 700, color: colors.textSecondary,
                  borderBottom: `1px solid ${colors.border}`,
                }}>
                  Dönüştürülecek Kayıtlar
                </div>
                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#fff', borderBottom: `1px solid ${colors.border}`, position: 'sticky', top: 0 }}>
                        <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: colors.textSecondary }}>Tarih</th>
                        <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: colors.textSecondary }}>Eski Fiyat</th>
                        <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: colors.textSecondary }}>Yeni Fiyat</th>
                        <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: colors.textSecondary }}>Eski Miktar</th>
                        <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: colors.textSecondary }}>Yeni Miktar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.donusturulecek.map(row => (
                        <tr key={row.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '6px 10px', color: colors.textSecondary }}>{row.tarih}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', color: colors.textTertiary, textDecoration: 'line-through' }}>
                            {Number(row.eski_birim_fiyat).toFixed(4)}
                          </td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: colors.success }}>
                            {Number(row.yeni_birim_fiyat).toFixed(4)}
                          </td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', color: colors.textTertiary, textDecoration: 'line-through' }}>
                            {Number(row.eski_miktar).toFixed(4)}
                          </td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: colors.success }}>
                            {Number(row.yeni_miktar).toFixed(4)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div style={{
                padding: '16px', background: colors.backgroundAlt,
                borderRadius: radius.md, marginBottom: 16, textAlign: 'center',
                color: colors.textSecondary, fontSize: 14,
              }}>
                Dönüştürülecek kayıt bulunamadı.
              </div>
            )}
          </>
        )}

        {/* Submit error */}
        {submitError && (
          <div style={{
            padding: '8px 12px', borderRadius: radius.sm,
            background: colors.dangerLight, color: colors.danger,
            marginBottom: 12, fontSize: 13,
          }}>
            Dönüşüm hatası: {submitError}
          </div>
        )}

        {/* Butonlar */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '10px 20px', background: '#e0e0e0', color: '#333',
              border: 'none', borderRadius: radius.md, cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: 14, fontWeight: 600, opacity: submitting ? 0.6 : 1,
            }}
          >
            İptal
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            style={{
              padding: '10px 20px',
              background: canConfirm ? '#1a73e8' : '#bdbdbd',
              color: '#fff', border: 'none', borderRadius: radius.md,
              cursor: canConfirm ? 'pointer' : 'not-allowed',
              fontSize: 14, fontWeight: 700,
            }}
          >
            {submitting ? 'Dönüştürülüyor...' : 'Dönüştür'}
          </button>
        </div>
      </div>
    </div>
  );
}
